import { useState } from 'react'
import { View, Text } from 'react-native'
import { BrandedRefreshControl } from '@/components/ui/BrandedRefreshControl'
import { ScrollView } from 'react-native-gesture-handler'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useScreenInsets } from '@/lib/hooks/use-screen-insets'
import Animated from 'react-native-reanimated'
import {
  Plus,
  Car,
  CarFront,
  Camera,
  DollarSign,
  FileCheck,
  Star,
  TrendingUp,
  Clock,
  Landmark,
  ChevronRight,
} from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { Image as AppImage } from '@/components/ui/Image'
import { Avatar } from '@/components/ui/Avatar'
import { AdaptiveRow } from '@/components/ui/AdaptiveRow'
import { Skeleton } from '@/components/ui/Skeleton'
import { OrgBanner } from '@/components/hosting/OrgBanner'
import { HostRetentionCard } from '@/components/hosting/HostRetentionCard'
import { HostVehicleActions } from '@/components/hosting/HostVehicleActions'
import { HostRentalTermsCard } from '@/components/legal/HostRentalTermsCard'
import { useAuth } from '@/lib/hooks/use-auth'
import { useHostVehicles } from '@/lib/hooks/use-host-vehicles'
import { useHostingEligibility } from '@/lib/hooks/use-hosting-eligibility'
import { useHostStats, useRecentBookings } from '@/lib/hooks/use-hosting-dashboard'
import { useOpenListingWizard, useEditListingGate } from '@/lib/hooks/use-open-listing-wizard'
import { expiredVehicleDocuments } from '@/lib/utils/vehicle-booking-blockers'
import { ResumeDraftDialog } from '@/components/hosting/ResumeDraftDialog'
import { EditDiscardsDraftDialog } from '@/components/hosting/EditDiscardsDraftDialog'
import { useSemanticColors, withAlpha, SPACING, RADIUS, TEXT_STYLES } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { useTheme } from '@/lib/hooks/use-theme'
import { useEntrance, type EntranceFn } from '@/lib/hooks/use-entrance'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import { PageShell } from '@/components/layout/PageShell'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import type { RecentBooking } from '@/lib/types/hosting.types'
import { formatCurrency, formatPrice } from '@/lib/utils/format'
import { formatBookingDate, formatSvDateDisplay } from '@/lib/utils/dates'

function StatCard({
  icon: Icon,
  label,
  value,
  colors,
}: {
  icon: typeof Car
  label: string
  value: string | number
  colors: ReturnType<typeof useSemanticColors>
}) {
  return (
    <View
      style={{
        // The card no longer claims the flex share itself — `AdaptiveRow` owns
        // it, because `flex: 1` is only correct while these are columns. In a
        // stack it would mean "take a third of the HEIGHT", which in an
        // auto-height parent collapses the card to nothing.
        flexGrow: 1,
        backgroundColor: colors.surface,
        borderRadius: RADIUS.lg,
        padding: SPACING.md,
        alignItems: 'center',
        gap: SPACING.xs,
        ...WEB_SHADOWS.sm,
      }}
    >
      <Icon size={22} color={COLORS.coral.DEFAULT} strokeWidth={1.8} />
      <Text style={{ fontSize: 20, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
        {value}
      </Text>
      <Text
        style={{
          fontSize: 12,
          fontFamily: FONTS.body,
          color: colors.textSecondary,
          textAlign: 'center',
        }}
      >
        {label}
      </Text>
    </View>
  )
}

function HostingLaunchCard({
  colors,
  onPress,
}: {
  colors: ReturnType<typeof useSemanticColors>
  onPress: () => void
}) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const steps = [
    {
      key: 'photos',
      icon: Camera,
      label: t('hosting.empty_step_photos'),
      backgroundColor: isDark ? 'rgba(240,66,45,0.20)' : 'rgba(240,66,45,0.12)',
      color: colors.textAccent,
    },
    {
      key: 'docs',
      icon: FileCheck,
      label: t('hosting.empty_step_docs'),
      backgroundColor: isDark ? 'rgba(124,58,237,0.22)' : 'rgba(72,12,130,0.12)',
      color: isDark ? '#B879FF' : COLORS.purple.DEFAULT,
    },
    {
      key: 'price',
      icon: DollarSign,
      label: t('hosting.empty_step_price'),
      backgroundColor: isDark ? 'rgba(249,168,37,0.22)' : 'rgba(249,168,37,0.16)',
      color: isDark ? '#FFB74D' : COLORS.amber.dark,
    },
  ]

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={t('hosting.empty_launch_title')}
      testID="hosting.empty-launch-card"
      style={{
        borderRadius: RADIUS.lg,
        padding: SPACING.lg,
        gap: SPACING.md,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
        ...WEB_SHADOWS.sm,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.md }}>
        <View
          style={{
            width: 56,
            height: 56,
            borderRadius: RADIUS.lg,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: COLORS.coral.dark,
          }}
        >
          <CarFront size={28} color="#FFFFFF" strokeWidth={2.1} />
        </View>
        <View style={{ flex: 1, gap: 3 }}>
          <Text
            style={{
              fontSize: 18,
              lineHeight: 24,
              fontFamily: FONTS.bodyBold,
              color: colors.textPrimary,
            }}
          >
            {t('hosting.empty_launch_title')}
          </Text>
          <Text
            style={{
              fontSize: 13,
              lineHeight: 19,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
            }}
          >
            {t('hosting.empty_launch_desc')}
          </Text>
        </View>
        <ChevronRight size={20} color={COLORS.coral.DEFAULT} strokeWidth={2} />
      </View>

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.sm }}>
        {steps.map(({ key, icon: Icon, label, backgroundColor, color }) => (
          <View
            key={key}
            style={{
              minHeight: 38,
              borderRadius: RADIUS.md,
              paddingHorizontal: SPACING.sm,
              flexDirection: 'row',
              alignItems: 'center',
              gap: SPACING.xs,
              backgroundColor,
            }}
          >
            <Icon size={15} color={color} strokeWidth={2} />
            <Text
              style={{
                fontSize: 12,
                lineHeight: 16,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
              }}
            >
              {label}
            </Text>
          </View>
        ))}
      </View>
    </Pressable>
  )
}

function HostingDraftCard({
  colors,
  onPress,
}: {
  colors: ReturnType<typeof useSemanticColors>
  onPress: () => void
}) {
  const { t } = useTranslation()
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={t('hosting.resume_draft_title')}
      testID="hosting.resume-draft-card"
      style={{
        borderRadius: RADIUS.lg,
        padding: SPACING.lg,
        gap: SPACING.sm,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: COLORS.coral.DEFAULT,
        ...WEB_SHADOWS.sm,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.md }}>
        <View
          style={{
            width: 48,
            height: 48,
            borderRadius: RADIUS.lg,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: withAlpha(COLORS.coral.DEFAULT, 0.14),
          }}
        >
          <CarFront size={24} color={COLORS.coral.DEFAULT} strokeWidth={2} />
        </View>
        <View style={{ flex: 1, gap: 3 }}>
          <Text
            style={{
              fontSize: 17,
              lineHeight: 23,
              fontFamily: FONTS.bodyBold,
              color: colors.textPrimary,
            }}
          >
            {t('hosting.resume_draft_title')}
          </Text>
          <Text
            style={{
              fontSize: 13,
              lineHeight: 19,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
            }}
          >
            {t('hosting.resume_draft_body')}
          </Text>
        </View>
        <ChevronRight size={20} color={COLORS.coral.DEFAULT} strokeWidth={2} />
      </View>
    </Pressable>
  )
}

function HostRentalStatusPill({
  status,
  colors,
}: {
  status: RecentBooking['status']
  colors: ReturnType<typeof useSemanticColors>
}) {
  const { t } = useTranslation()
  const tone = (() => {
    switch (status) {
      case 'pending_payment':
        return { bg: `${COLORS.coral.DEFAULT}14`, fg: COLORS.coral.DEFAULT }
      case 'confirmed':
      case 'pending_pickup':
        return { bg: `${COLORS.amber.DEFAULT}20`, fg: COLORS.amber.dark }
      case 'active':
      case 'pending_return':
        return { bg: `${colors.success}18`, fg: colors.success }
      case 'completed':
        return { bg: colors.surfaceSubtle, fg: colors.textSecondary }
      case 'cancelled':
      case 'disputed':
        return { bg: colors.surfaceSubtle, fg: colors.textMuted }
      default:
        return { bg: colors.surfaceSubtle, fg: colors.textSecondary }
    }
  })()

  return (
    <View
      style={{
        alignSelf: 'flex-start',
        paddingHorizontal: SPACING.sm,
        paddingVertical: 4,
        borderRadius: RADIUS.full,
        backgroundColor: tone.bg,
      }}
    >
      <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: tone.fg }} numberOfLines={1}>
        {t(`hosting.booking_status_${status}`)}
      </Text>
    </View>
  )
}

function RecentRentalsSection({
  bookings,
  isLoading,
  colors,
  onOpen,
  enter,
}: {
  bookings: RecentBooking[]
  isLoading: boolean
  colors: ReturnType<typeof useSemanticColors>
  onOpen: (bookingId: string) => void
  /** Owned by the screen, not by this section: the latch must outlive the remount. */
  enter: EntranceFn
}) {
  const { t, i18n } = useTranslation()
  const formatDate = (iso: string) =>
    formatBookingDate(iso, i18n.language, { month: 'short', day: 'numeric' })

  return (
    <Animated.View collapsable={false} entering={enter(4)}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          marginBottom: SPACING.sm,
          gap: SPACING.md,
        }}
      >
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: 16, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
            {t('hosting.recent_rentals')}
          </Text>
          <Text
            style={{
              marginTop: 2,
              fontSize: 12,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
            }}
          >
            {t('hosting.recent_rentals_subtitle')}
          </Text>
        </View>
      </View>

      {isLoading ? (
        <View style={{ gap: SPACING.sm }}>
          <Skeleton width="100%" height={72} borderRadius={RADIUS.md} />
          <Skeleton width="100%" height={72} borderRadius={RADIUS.md} />
        </View>
      ) : bookings.length === 0 ? (
        <View
          style={{
            padding: SPACING.md,
            borderRadius: RADIUS.md,
            backgroundColor: colors.surface,
            borderWidth: 1,
            borderColor: colors.border,
          }}
        >
          <Text style={{ fontSize: 14, fontFamily: FONTS.body, color: colors.textSecondary }}>
            {t('hosting.recent_rentals_empty')}
          </Text>
        </View>
      ) : (
        <View style={{ gap: SPACING.sm }} testID="hosting.recent-rentals">
          {bookings.map((booking) => (
            <Pressable
              key={booking.id}
              onPress={() => onOpen(booking.id)}
              accessibilityRole="button"
              accessibilityLabel={`${booking.vehicleTitle}, ${t(
                `hosting.booking_status_${booking.status}`,
              )}`}
              testID={`hosting.recent-rental-${booking.id}`}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: SPACING.md,
                padding: SPACING.md,
                borderRadius: RADIUS.md,
                backgroundColor: colors.surface,
                borderWidth: 1,
                borderColor: colors.border,
                ...WEB_SHADOWS.sm,
              }}
            >
              <Avatar
                size="md"
                source={booking.guestAvatarUrl}
                fallback={booking.guestDisplayName ?? t('vehicle_detail.guest')}
                accessibilityLabel={booking.guestDisplayName ?? t('vehicle_detail.guest')}
              />
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text
                  style={{ fontSize: 15, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}
                  numberOfLines={1}
                >
                  {booking.vehicleTitle}
                </Text>
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: SPACING.xs,
                    marginTop: 3,
                  }}
                >
                  <Clock size={13} color={colors.textSecondary} strokeWidth={1.8} />
                  <Text
                    style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textSecondary }}
                    numberOfLines={1}
                  >
                    {formatDate(booking.startAt)}
                    {' -> '}
                    {formatDate(booking.endAt)}
                  </Text>
                </View>
                <View style={{ marginTop: SPACING.xs }}>
                  <HostRentalStatusPill status={booking.status} colors={colors} />
                </View>
              </View>
              <ChevronRight size={18} color={colors.textMuted} strokeWidth={1.8} />
            </Pressable>
          ))}
        </View>
      )}
    </Animated.View>
  )
}

export default function HostingScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useScreenInsets()
  const { isDesktop } = useBreakpoint()
  const { user } = useAuth()
  const {
    organizations,
    currentOrganization,
    isLoading: isOrganizationsLoading,
  } = useHostingEligibility()
  const enter = useEntrance(!isOrganizationsLoading)

  const hasMultipleOrgs = organizations.length > 1
  const [activeOrgId, setActiveOrgId] = useState<string | null>(null)
  const primaryOrg =
    (activeOrgId ? organizations.find((o) => o.id === activeOrgId) : null) ?? currentOrganization
  const canManageOrgPayouts =
    primaryOrg?.myRole === 'fleet_manager' || primaryOrg?.myRole === 'admin'
  const { vehicles, isLoading, isRefetching, refetch } = useHostVehicles(primaryOrg?.id ?? null)
  const { stats } = useHostStats(user?.id ?? '', primaryOrg?.id ?? null)
  const { bookings: recentBookings, isLoading: isRecentBookingsLoading } = useRecentBookings(
    user?.id ?? '',
    4,
    primaryOrg?.id ?? null,
  )

  const hasVehicles = vehicles.length > 0
  const openWizard = useOpenListingWizard()
  const [showResumeDraft, setShowResumeDraft] = useState(false)
  // "Agregar vehículo" used to call reset() unconditionally, which overwrites the
  // persisted draft synchronously — so the one button a returning host taps to
  // resume was the button that destroyed 17 live photos, silently and with no
  // undo. Ask instead; only an explicit "empezar de cero" discards (and only
  // discardDraft frees the captured files).
  const handleAddVehicle = () => {
    if (openWizard.hasDraft) {
      setShowResumeDraft(true)
      return
    }
    openWizard.startFresh()
  }
  // Tapping your own car is the most natural gesture on this screen, and it used
  // to bare-reset the wizard — destroying a persisted 17-photo draft with no
  // dialog, while the wizard header still promised autosave. Shared gate.
  const editGate = useEditListingGate()
  const handleEditVehicle = editGate.openForEdit

  // The container and the page shell render from ONE JSX position each. The
  // loading branch used to re-wrap its own copies, so the whole dashboard was
  // torn down and rebuilt on the organizations loading -> content transition.
  //
  // No ScreenState here on purpose: the gate is `useHostingEligibility`, a
  // derived view over the organizations query with no error branch of its
  // own, and "this host has no organization" is a legitimate content state
  // (individual hosting). The vehicle list and recent-rentals sections below
  // keep their own inline skeletons.
  return (
    <ScreenContainer variant="dashboard" scroll={false}>
      <PageShell title={t('hosting.title')}>
        {isOrganizationsLoading ? (
          <View style={{ padding: isDesktop ? 0 : SPACING.lg, gap: SPACING.md }}>
            <Skeleton width="55%" height={28} borderRadius={RADIUS.sm} />
            <Skeleton width="100%" height={104} borderRadius={RADIUS.lg} />
            <Skeleton width="100%" height={52} borderRadius={RADIUS.md} />
          </View>
        ) : (
          <ScrollView
            contentContainerStyle={{
              // No `insets.top` here: ScreenContainer already pads the safe
              // area on mobile. Adding it again pushed "Anfitrión" a
              // status-bar lower than the "Viajes" / "Mensajes" titles, which
              // is why the three tab roots did not share a top edge.
              paddingTop: isDesktop ? 0 : SPACING.sm,
              paddingBottom: insets.bottom + SPACING.xl,
              paddingHorizontal: isDesktop ? 0 : SPACING.lg,
              // One rhythm for the whole dashboard. Every top-level block used
              // to carry its own marginTop and they disagreed (lg, md, lg,
              // md, lg), so a 16pt gap landed in the middle of a 24pt stack
              // with no grouping cue to justify it.
              gap: SPACING.lg,
            }}
            showsVerticalScrollIndicator={false}
            refreshControl={<BrandedRefreshControl refreshing={isRefetching} onRefresh={refetch} />}
          >
            {/* Mobile-only title — desktop title lives in PageShell */}
            {!isDesktop && (
              <Animated.View collapsable={false} entering={enter(1)}>
                <Text
                  style={{
                    ...TEXT_STYLES.h1,
                    fontFamily: FONTS.bodyBold,
                    color: colors.textPrimary,
                  }}
                >
                  {t('hosting.title')}
                </Text>
              </Animated.View>
            )}

            {/* Organization switcher — only when user belongs to 2+ orgs */}
            {hasMultipleOrgs && (
              <Animated.View collapsable={false} entering={enter(1)}>
                <Text
                  style={{
                    fontSize: 12,
                    fontFamily: FONTS.body,
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                    marginBottom: SPACING.xs,
                  }}
                >
                  {t('org.active_org')}
                </Text>
                {/* On desktop chips wrap into rows — a horizontal scroll gesture
                on a 1200px dashboard reads as "ported mobile app". Native
                stays with `ScrollView horizontal` so touch-to-pan works on
                narrow viewports where the chips genuinely don't fit. */}
                {isDesktop ? (
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.sm }}>
                    {organizations.map((org) => {
                      const isActive = primaryOrg?.id === org.id
                      return (
                        <Pressable
                          key={org.id}
                          onPress={() => setActiveOrgId(org.id)}
                          accessibilityRole="button"
                          accessibilityState={{ selected: isActive }}
                          testID={`hosting.org-switcher-${org.id}`}
                          style={{
                            paddingVertical: SPACING.sm,
                            paddingHorizontal: SPACING.md,
                            borderRadius: RADIUS.full,
                            borderWidth: isActive ? 2 : 1,
                            borderColor: isActive ? COLORS.coral.DEFAULT : colors.border,
                            backgroundColor: isActive
                              ? `${COLORS.coral.DEFAULT}10`
                              : colors.surface,
                          }}
                        >
                          <Text
                            style={{
                              fontSize: 13,
                              fontFamily: isActive ? FONTS.bodyBold : FONTS.body,
                              color: isActive ? colors.textAccent : colors.textSecondary,
                            }}
                            numberOfLines={1}
                          >
                            {org.name}
                          </Text>
                        </Pressable>
                      )
                    })}
                  </View>
                ) : (
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    contentContainerStyle={{ gap: SPACING.sm }}
                  >
                    {organizations.map((org) => {
                      const isActive = primaryOrg?.id === org.id
                      return (
                        <Pressable
                          key={org.id}
                          onPress={() => setActiveOrgId(org.id)}
                          accessibilityRole="button"
                          accessibilityState={{ selected: isActive }}
                          testID={`hosting.org-switcher-${org.id}`}
                          style={{
                            paddingVertical: SPACING.sm,
                            paddingHorizontal: SPACING.md,
                            borderRadius: RADIUS.full,
                            borderWidth: isActive ? 2 : 1,
                            borderColor: isActive ? COLORS.coral.DEFAULT : colors.border,
                            backgroundColor: isActive
                              ? `${COLORS.coral.DEFAULT}10`
                              : colors.surface,
                          }}
                        >
                          <Text
                            style={{
                              fontSize: 13,
                              fontFamily: isActive ? FONTS.bodyBold : FONTS.body,
                              color: isActive ? colors.textAccent : colors.textSecondary,
                            }}
                            numberOfLines={1}
                          >
                            {org.name}
                          </Text>
                        </Pressable>
                      )
                    })}
                  </ScrollView>
                )}
              </Animated.View>
            )}

            {/* Organization Banner */}
            <HostRentalTermsCard />
            {primaryOrg && (
              <OrgBanner
                organization={primaryOrg}
                onPress={() => router.push(`/hosting/organization?orgId=${primaryOrg.id}` as never)}
                entering={enter(2)}
              />
            )}

            {/* Vehicle list or empty state */}
            <Animated.View collapsable={false} entering={enter(2)}>
              {isLoading ? (
                <View style={{ gap: SPACING.sm }}>
                  <Skeleton width="60%" height={20} borderRadius={RADIUS.sm} />
                  <Skeleton width="100%" height={72} borderRadius={RADIUS.md} />
                  <Skeleton width="100%" height={72} borderRadius={RADIUS.md} />
                </View>
              ) : !hasVehicles ? (
                openWizard.hasDraft ? (
                  <HostingDraftCard colors={colors} onPress={openWizard.resume} />
                ) : (
                  <HostingLaunchCard colors={colors} onPress={handleAddVehicle} />
                )
              ) : (
                <View testID="listings.vehicle-list">
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: SPACING.sm,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 16,
                        fontFamily: FONTS.bodyBold,
                        color: colors.textPrimary,
                        marginBottom: SPACING.sm,
                      }}
                    >
                      {t('hosting.my_listings')}
                    </Text>
                    <Pressable
                      onPress={() => router.push('/hosting/listings')}
                      accessibilityRole="button"
                      testID="hosting.all-vehicles"
                      style={{
                        minHeight: 44,
                        justifyContent: 'center',
                        paddingHorizontal: SPACING.sm,
                      }}
                    >
                      <Text
                        style={{
                          fontFamily: FONTS.bodyBold,
                          fontSize: 14,
                          color: colors.textBrand,
                        }}
                      >
                        {t('explore.view_all')}
                      </Text>
                    </Pressable>
                  </View>
                  {vehicles.slice(0, 3).map((vehicle, index) => {
                    const primaryImage =
                      vehicle.images.find((image) => image.isPrimary) ?? vehicle.images[0]
                    // Un carro con un papel vencido dice "Activo" y la base
                    // rechaza toda reserva. El aviso vive ACA y no en
                    // /hosting/listings porque esta pantalla la abrieron 556
                    // personas y aquella 9: un aviso correcto en la pantalla
                    // que nadie mira es un aviso que no existe.
                    const expiredDocs = expiredVehicleDocuments(vehicle)

                    return (
                      <View
                        key={vehicle.id}
                        style={{
                          marginBottom: SPACING.sm,
                          backgroundColor: colors.surface,
                          borderRadius: RADIUS.md,
                          ...WEB_SHADOWS.sm,
                        }}
                      >
                        <Pressable
                          onPress={() =>
                            handleEditVehicle(
                              vehicle.id,
                              expiredDocs.length > 0 ? 'documents' : undefined,
                            )
                          }
                          accessibilityRole="button"
                          accessibilityLabel={vehicle.title}
                          testID={`listings.vehicle-${index}`}
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            backgroundColor: colors.surface,
                            borderRadius: RADIUS.md,
                            padding: SPACING.md,
                            gap: SPACING.md,
                          }}
                        >
                          <View
                            style={{
                              width: 56,
                              height: 56,
                              borderRadius: RADIUS.md,
                              backgroundColor: colors.surfaceSubtle,
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                            }}
                          >
                            {primaryImage?.url ? (
                              <AppImage
                                source={primaryImage.url}
                                blurhash={primaryImage.blurhash ?? undefined}
                                width={56}
                                height={56}
                                borderRadius={RADIUS.md}
                                accessibilityLabel={vehicle.title}
                              />
                            ) : (
                              <Car size={24} color={colors.textSecondary} strokeWidth={1.5} />
                            )}
                          </View>
                          <View style={{ flex: 1 }}>
                            <Text
                              style={{
                                fontSize: 15,
                                fontFamily: FONTS.bodyBold,
                                color: colors.textPrimary,
                              }}
                              numberOfLines={1}
                            >
                              {vehicle.title}
                            </Text>
                            {expiredDocs.length > 0 ? (
                              // Reemplaza al estado, no lo acompana: decir
                              // "Activo · $65/dia" al lado de "no se puede
                              // reservar" es pedirle a la persona que decida cual
                              // de las dos frases le creemos nosotros.
                              <Text
                                style={{
                                  fontSize: 13,
                                  fontFamily: FONTS.bodyBold,
                                  color: colors.textAccent,
                                  marginTop: 2,
                                }}
                                numberOfLines={2}
                              >
                                {t('hosting.expired_doc_title')} ·{' '}
                                {t('hosting.expired_doc_short', {
                                  date: formatSvDateDisplay(expiredDocs[0]!.expiryDate),
                                })}
                              </Text>
                            ) : (
                              <Text
                                style={{
                                  fontSize: 13,
                                  fontFamily: FONTS.body,
                                  color: colors.textSecondary,
                                  marginTop: 2,
                                }}
                              >
                                {t(`hosting.status_${vehicle.status}`)} ·{' '}
                                {formatPrice(
                                  vehicle.category === 'heavy_duty' && vehicle.hourlyRateCents
                                    ? vehicle.hourlyRateCents
                                    : (vehicle.dailyRateCents ?? 0),
                                )}
                                {vehicle.category === 'heavy_duty'
                                  ? t('vehicle_detail.per_hour')
                                  : t('vehicle_detail.per_day')}
                              </Text>
                            )}
                          </View>
                        </Pressable>
                        <HostVehicleActions
                          index={index}
                          onEdit={() => handleEditVehicle(vehicle.id)}
                          onCalendar={() =>
                            router.push(
                              `/hosting/calendar/${vehicle.id}?title=${encodeURIComponent(vehicle.title)}`,
                            )
                          }
                        />
                      </View>
                    )
                  })}
                </View>
              )}
            </Animated.View>

            <Animated.View collapsable={false} entering={enter(2)}>
              <HostRetentionCard />
            </Animated.View>

            {/* Stats Row */}
            {stats && (
              <Animated.View collapsable={false} entering={enter(2)}>
                {/* A third of a 448dp phone is 128dp, and at 2x system text the
                    single word "Vehículos" is wider than that — so Android broke
                    the labels INSIDE the words: "Vehí / culos / activos",
                    "Reserv / as pen / dientes", "Califica / ción pr / omedio".
                    Nothing about a one-third column can be grown to fix that, so
                    past 1.5x the three cards stop being columns and become rows.
                    At scale 1 this renders the approved row unchanged. */}
                <AdaptiveRow gap={SPACING.sm}>
                  <StatCard
                    icon={Car}
                    label={t('hosting.active_listings')}
                    value={stats.activeListings}
                    colors={colors}
                  />
                  <StatCard
                    icon={Clock}
                    label={t('hosting.pending_bookings')}
                    value={stats.pendingBookings}
                    colors={colors}
                  />
                  <StatCard
                    icon={Star}
                    label={t('hosting.avg_rating')}
                    value={stats.avgRating?.toFixed(1) ?? '—'}
                    colors={colors}
                  />
                </AdaptiveRow>
              </Animated.View>
            )}

            {hasVehicles && (
              <RecentRentalsSection
                bookings={recentBookings}
                isLoading={isRecentBookingsLoading}
                colors={colors}
                onOpen={(bookingId) => router.push(`/trips/${bookingId}` as never)}
                enter={enter}
              />
            )}

            {/* Earnings card */}
            {stats && stats.totalEarningsCents > 0 && (
              <Animated.View collapsable={false} entering={enter(3)}>
                <Pressable
                  onPress={() => router.push('/hosting/earnings')}
                  accessibilityRole="button"
                  accessibilityLabel={t('hosting.see_earnings')}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    backgroundColor: colors.surface,
                    borderRadius: RADIUS.lg,
                    padding: SPACING.md,
                    gap: SPACING.md,
                    ...WEB_SHADOWS.sm,
                  }}
                >
                  <View
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 22, // circle = 44 / 2
                      backgroundColor: `${COLORS.coral.DEFAULT}15`,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <TrendingUp size={22} color={COLORS.coral.DEFAULT} strokeWidth={2} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text
                      style={{ fontSize: 14, fontFamily: FONTS.body, color: colors.textSecondary }}
                    >
                      {t('hosting.total_earnings')}
                    </Text>
                    <Text
                      style={{
                        fontSize: 22,
                        fontFamily: FONTS.bodyBold,
                        color: colors.textPrimary,
                      }}
                    >
                      {formatCurrency(stats.totalEarningsCents ?? 0)}
                    </Text>
                  </View>
                  <DollarSign size={18} color={colors.textSecondary} strokeWidth={2} />
                </Pressable>
              </Animated.View>
            )}

            {/* Add vehicle CTA */}
            <Animated.View collapsable={false} entering={enter(4)}>
              <Pressable
                onPress={handleAddVehicle}
                accessibilityRole="button"
                accessibilityLabel={t('hosting.add_vehicle')}
                testID="hosting.add-vehicle"
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: SPACING.sm,
                  height: 52,
                  borderRadius: RADIUS.md,
                  backgroundColor: COLORS.coral.dark,
                }}
              >
                <Plus size={20} color="#FFFFFF" strokeWidth={2.5} />
                <Text
                  style={{ fontSize: 16, fontFamily: FONTS.bodyBold, color: '#FFFFFF' }}
                  numberOfLines={1}
                  adjustsFontSizeToFit
                >
                  {t('hosting.add_vehicle')}
                </Text>
              </Pressable>
            </Animated.View>

            {(!primaryOrg || canManageOrgPayouts) && (
              <Animated.View collapsable={false} entering={enter(5)}>
                <Pressable
                  onPress={() =>
                    primaryOrg
                      ? router.push(`/hosting/organization/payouts?orgId=${primaryOrg.id}` as never)
                      : router.push('/hosting/payouts' as never)
                  }
                  accessibilityRole="button"
                  accessibilityLabel={t('payouts.manage_accounts')}
                  testID="hosting.manage-payouts"
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: SPACING.md,
                    padding: SPACING.md,
                    borderRadius: RADIUS.md,
                    backgroundColor: colors.surface,
                    borderWidth: 1,
                    borderColor: colors.border,
                    ...WEB_SHADOWS.sm,
                  }}
                >
                  {/* Purple in BOTH themes, like the identical row on
                      Ganancias. The dark branch used to paint this tile coral
                      on coral directly under the coral "Agregar vehículo"
                      button, so a navigation row read as a second primary
                      action. `textBrand` is the brand hue at a lightness that
                      survives a dark surface; the tile is that same hue at low
                      alpha, so the pair holds in either theme. */}
                  <View
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: RADIUS.md,
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: withAlpha(colors.textBrand, isDark ? 0.18 : 0.1),
                    }}
                  >
                    <Landmark size={20} color={colors.textBrand} strokeWidth={1.8} />
                  </View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text
                      style={{
                        fontSize: 15,
                        fontFamily: FONTS.bodyBold,
                        color: colors.textPrimary,
                      }}
                    >
                      {t('payouts.manage_accounts')}
                    </Text>
                    {/* No clamp. With `numberOfLines={2}` this sentence was
                        cut inside a word on the first line — "…tu cuenta b…" —
                        while the card already had the height for the second
                        one. The identical row on Ganancias has no clamp and
                        wraps cleanly; this one now matches it. */}
                    <Text
                      style={{
                        fontSize: 12,
                        lineHeight: 17,
                        fontFamily: FONTS.body,
                        color: colors.textSecondary,
                        marginTop: SPACING.xs,
                      }}
                    >
                      {t('payouts.manage_accounts_desc')}
                    </Text>
                  </View>
                  <ChevronRight size={18} color={colors.textMuted} strokeWidth={1.7} />
                </Pressable>
              </Animated.View>
            )}
          </ScrollView>
        )}
      </PageShell>

      {/* The host decides what happens to their own work. Continuing touches
          nothing; only "empezar de cero" discards, and only that path frees the
          captured files. */}
      <EditDiscardsDraftDialog {...editGate.dialogProps} testIDPrefix="hosting" />

      <ResumeDraftDialog
        visible={showResumeDraft}
        onResume={() => {
          setShowResumeDraft(false)
          openWizard.resume()
        }}
        onStartOver={() => {
          setShowResumeDraft(false)
          openWizard.startOver()
        }}
        onDismiss={() => setShowResumeDraft(false)}
        testIDPrefix="hosting"
      />
    </ScreenContainer>
  )
}
